import {
  Box,
  Button,
  ClickAwayListener,
  Paper,
  Popper,
  Typography,
  alpha,
  drawerClasses,
} from "@mui/material"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { pxToRem } from "@sap/theme/typography"

export default function ImageSearchAnnouncement({
  id,
  anchorEl,
  onDismiss,
}: {
  id: string
  anchorEl: HTMLElement
  onDismiss: () => void
}) {
  const { t } = useTranslation("common", { keyPrefix: "imageSearch.announcement" })
  const titleId = `${id}-title`
  const descriptionId = `${id}-description`
  const [arrowEl, setArrowEl] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.stopPropagation()
        onDismiss()
      }
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onDismiss])

  return (
    <Popper
      open
      anchorEl={anchorEl}
      container={anchorEl.closest(`.${drawerClasses.paper}`) ?? undefined}
      placement="bottom-end"
      modifiers={[
        { name: "offset", options: { offset: [0, 14] } },
        { name: "preventOverflow", options: { padding: 16 } },
        { name: "arrow", options: { element: arrowEl, padding: 12 } },
      ]}
      sx={{
        zIndex: (theme) => theme.zIndex.tooltip,
        '&[data-popper-placement^="top"] .announcement-arrow': {
          top: "auto",
          bottom: -6,
        },
      }}
    >
      <ClickAwayListener onClickAway={onDismiss}>
        <Paper
          role="dialog"
          aria-modal="false"
          aria-labelledby={titleId}
          aria-describedby={descriptionId}
          sx={{
            position: "relative",
            width: pxToRem(250),
            maxWidth: "calc(100vw - 32px)",
            p: 1.75,
            borderRadius: 1.5,
            bgcolor: "grey.900",
            color: "common.white",
            boxShadow: (theme) => `0 6px 18px ${alpha(theme.palette.common.black, 0.2)}`,
          }}
        >
          <Box
            className="announcement-arrow"
            ref={setArrowEl}
            aria-hidden="true"
            sx={{
              position: "absolute",
              top: -6,
              width: 12,
              height: 12,
              "&::before": {
                content: '""',
                display: "block",
                width: "100%",
                height: "100%",
                bgcolor: "grey.900",
                transform: "rotate(45deg)",
              },
            }}
          />
          <Typography id={titleId} sx={{ fontSize: pxToRem(14), fontWeight: 700 }}>
            {t("title")}
          </Typography>
          <Typography id={descriptionId} sx={{ mt: 0.5, fontSize: pxToRem(13), lineHeight: 1.5 }}>
            {t("description")}
          </Typography>
          <Button
            size="small"
            onClick={() => {
              onDismiss()
              anchorEl.focus()
            }}
            sx={{
              mt: 1.25,
              minWidth: 0,
              px: 1.25,
              bgcolor: "common.white",
              color: "grey.900",
              fontSize: pxToRem(12),
              fontWeight: 500,
              "&:hover": { bgcolor: "grey.200" },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "common.white",
                outlineOffset: 2,
              },
            }}
          >
            {t("dismiss")}
          </Button>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}
